import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import { api, formatApiError } from "../lib/api";

export default function Vault() {
  const { token } = useParams();
  if (token) return <VaultView token={token} />;
  return <VaultLogin />;
}

function VaultLogin() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/vault/request", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="vault-login">
      <Helmet><title>Client Vault — Abril Suarez</title></Helmet>
      <Header />
      <section className="px-6 sm:px-10 pt-32 pb-24 max-w-xl mx-auto">
        <p className="overline text-[#8B6D5E] mb-4">— Client Vault</p>
        <h1 className="font-serif text-5xl sm:text-6xl leading-[1.02] mb-4">
          Tu <em className="text-[#8B6D5E]">portal privado</em>.
        </h1>
        <p className="text-lg text-[#3D3D3D] mb-10 leading-relaxed">
          Acceso reservado a clientas de Abril: tu historial de citas, tus looks, y re-agendar
          con un solo click.
        </p>

        {sent ? (
          <div className="border border-[#E8E1DA] p-8 bg-[#F7F0E8]" data-testid="vault-sent">
            <p className="overline text-[#8B6D5E] mb-3">Link enviado</p>
            <p className="font-serif text-2xl mb-3">Revisa tu correo.</p>
            <p className="text-[#3D3D3D]">
              Si el email <strong>{email}</strong> tiene citas contigo, recibirás en los próximos
              minutos un link privado para abrir tu Vault. Por seguridad, no reveleremos si tu email existe o no.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <label className="overline block text-[#8B6D5E]">Tu email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              className="w-full border border-[#E8E1DA] px-4 py-3 bg-[#FAFAFA] font-serif text-lg focus:outline-none focus:border-[#1A1A1A]"
              data-testid="vault-email-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1A1A1A] text-[#FAFAFA] px-6 py-4 overline hover:bg-[#8B6D5E] transition-colors disabled:opacity-50"
              data-testid="vault-submit"
            >
              {loading ? "Enviando..." : "Recibir mi link privado →"}
            </button>
            <p className="text-xs text-[#5C5C5C]">
              Enviamos un link único a tu correo. No usa contraseña.
            </p>
          </form>
        )}
      </section>
      <Footer />
    </div>
  );
}

function VaultView({ token }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/vault/${token}`).then(({ data }) => setData(data)).catch((e) => setErr(formatApiError(e)));
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <section className="px-6 py-32 max-w-xl mx-auto text-center">
          <p className="overline text-[#8B6D5E] mb-4">— Link inválido</p>
          <p className="font-serif text-3xl mb-6">Este link no es válido o expiró.</p>
          <Link to="/vault" className="overline link-underline">Solicitar uno nuevo</Link>
        </section>
        <Footer />
      </div>
    );
  }
  if (!data) return null;

  const firstName = data.appointments[0]?.client_name?.split(" ")[0] || "";
  const tier = data.count >= 5 ? "Signature" : data.count >= 2 ? "Preferente" : "Cliente";

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="vault-view">
      <Helmet><title>{`Mi Vault — ${data.email}`}</title></Helmet>
      <Header />

      {/* VIP hero */}
      <section className="bg-[#1A1A1A] text-[#FAFAFA] px-6 sm:px-10 pt-32 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-3 border border-[#8B6D5E]/40 px-4 py-2 mb-8">
            <span className="w-1.5 h-1.5 bg-[#D9BE8E] rounded-full" />
            <p className="text-[11px] tracking-[0.28em] uppercase text-[#D9BE8E]">
              VIP <span className="text-[#8B6D5E] mx-1">|</span> Miembro {tier}
            </p>
          </div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] mb-4">
            {firstName ? `${firstName},` : "Hola,"} <em className="text-[#D9BE8E]">un placer verte de nuevo.</em>
          </h1>
          <p className="text-lg text-[#B8B8B8] max-w-2xl leading-relaxed">
            Aquí guardamos todo lo tuyo — tu piel, tus paletas, tus tiempos.
            Como cliente {tier.toLowerCase()}, la próxima vez que reserves,
            ya sabemos exactamente cómo te gusta.
          </p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-[#333] pt-8">
            <div>
              <p className="font-serif text-3xl text-[#D9BE8E]">{data.count}</p>
              <p className="text-[11px] tracking-[0.2em] uppercase text-[#8B8B8B] mt-1">Servicios</p>
            </div>
            <div>
              <p className="font-serif text-3xl text-[#D9BE8E]">Guardado</p>
              <p className="text-[11px] tracking-[0.2em] uppercase text-[#8B8B8B] mt-1">Skin Profile</p>
            </div>
            <div>
              <p className="font-serif text-3xl text-[#D9BE8E]">Sí</p>
              <p className="text-[11px] tracking-[0.2em] uppercase text-[#8B8B8B] mt-1">Prioridad de agenda</p>
            </div>
            <div>
              <p className="font-serif text-3xl text-[#D9BE8E]">−10%</p>
              <p className="text-[11px] tracking-[0.2em] uppercase text-[#8B8B8B] mt-1">Clienta recurrente</p>
            </div>
          </div>

          <a
            href={`https://wa.me/message/SG2ELRTG32LVF1?text=${encodeURIComponent(
              `Hola Abril! Soy ${firstName || data.email}, quiero reservar mi próxima cita — ya sabes cómo me gusta ✨`
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-10 inline-flex items-center justify-between gap-6 bg-[#D9BE8E] text-[#1A1A1A] px-8 py-5 hover:bg-[#F0DEB4] transition-colors"
            data-testid="vault-book-next"
          >
            <span>
              <span className="text-[11px] tracking-[0.28em] uppercase block mb-1">Reservar mi próxima cita</span>
              <span className="font-serif text-2xl">Abrir WhatsApp con Abril</span>
            </span>
            <span className="text-3xl transition-transform group-hover:translate-x-1">→</span>
          </a>
        </div>
      </section>

      <section className="px-6 sm:px-10 py-16 max-w-4xl mx-auto">
        <p className="overline text-[#8B6D5E] mb-3">Tu historial</p>
        <h2 className="font-serif text-3xl sm:text-4xl mb-10">Tus citas con Abril.</h2>
        {data.appointments.length === 0 && (
          <p className="text-[#5C5C5C]">Aún no tienes citas registradas.</p>
        )}
        {data.appointments.map((a) => (
          <div key={a.id} className="border-t border-[#E8E1DA] py-8 grid md:grid-cols-[1fr_auto] gap-4 items-center" data-testid={`vault-appt-${a.id}`}>
            <div>
              <p className="overline text-[#8B6D5E]">
                {a.date} · {a.start_time} — {a.status}
              </p>
              <p className="font-serif text-2xl mt-2">{a.service_title}</p>
              {a.location_address && <p className="text-sm text-[#5C5C5C] mt-1">{a.location_address}</p>}
            </div>
            <div className="flex flex-col gap-2 items-start md:items-end">
              <a href={a.portal_url} target="_blank" rel="noopener noreferrer" className="overline link-underline text-[#3C6348]">
                Abrir Studio Pass →
              </a>
              <a
                href={`https://wa.me/message/SG2ELRTG32LVF1?text=${encodeURIComponent(`Hola Abril! Me gustaría re-agendar un servicio similar al del ${a.date}.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="overline link-underline"
              >
                Re-agendar con 1 click →
              </a>
            </div>
          </div>
        ))}
      </section>
      <Footer />
    </div>
  );
}
